import React, { useEffect, useRef, useState } from 'react';

import { PlusOutlined, SearchOutlined, ClearOutlined, QuestionCircleOutlined, UploadOutlined, FilterOutlined, DownOutlined, UpOutlined } from '@ant-design/icons';
import { useRecordContext } from '@shared/table/Context';
import { Button, App, Popover, Typography, Form, Tag, Grid } from 'antd';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

const { Text } = Typography;
const { useBreakpoint } = Grid;

import { commonStyles } from '../styles';

import { useRulesContext } from './Context';
import FilterComponent from './FilterComponent';
import RulesForm from './RulesForm';






function Content({
  supportRules,
  // 型別放寬：searchable 由上游(TableIndex/Rules)傳入，可為 boolean 或 SearchableConfig 物件
  searchable = /** @type {boolean | import('../TableIndex').SearchableConfig} */ (false),
  tableId,
  lastModified,
  filterHelpExample,
  filterDocsUrl,
  searchTooltipContent,
  newPath,
  onCreate,
  onImportClick,
  testIdPrefix,
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { notification } = App.useApp();
  const { setQuery , clearSelected ,loading, hasError, clearAllSettings, query } = useRecordContext();
  const { rulesInputNames, addRule } = useRulesContext();
  const [form] = Form.useForm();
  const PINNED_RULES_KEY = `pinnedRule-${tableId}`

  // 響應式斷點
  const screens = useBreakpoint();
  const isMobile = !screens.md; // < 768px

  // 篩選器折疊狀態（小螢幕預設折疊）
  const [isFilterCollapsed, setIsFilterCollapsed] = useState(true);

  // 獲取已套用的篩選條件（用於顯示 Tags）
  const getAppliedFilters = () => {
    if (!query || typeof query !== 'object') return [];
    return Object.entries(query)
      .filter(([key, value]) => key !== 'search' && value !== undefined && value !== null && value !== '')
      .map(([key, value]) => {
        const rule = supportRules.find(r => r.inputName === key);
        const label = rule?.label || key;
        const displayValue = Array.isArray(value) ? value.join(', ') : String(value);
        return { key, label, value: displayValue };
      });
  };

  const appliedFilters = getAppliedFilters();

  // Standardized Create Button
  const renderCreateButton = () => {
    if (!newPath && !onCreate) return null;

    return (
      <Button
        type="primary"
        icon={<PlusOutlined />}
        onClick={() => {
          if (onCreate) {
            onCreate();
          } else if (newPath) {
            navigate(newPath);
          }
        }}
        {...(testIdPrefix && { 'data-testid': `${testIdPrefix}-create-btn` })}
      >
        {t('common.button.new')}
      </Button>
    );
  };

  // Standardized Import Button
  const renderImportButton = () => {
    if (!onImportClick) return null;

    return (
      <Button
        icon={<UploadOutlined />}
        onClick={onImportClick}
        {...(testIdPrefix && { 'data-testid': `${testIdPrefix}-import-btn` })}
      >
        {t('common.button.import')}
      </Button>
    );
  };

  useEffect(() => {
    if (lastModified) {
      form.submit()
    }
  }, [lastModified])

  useEffect(() => {
    const pinnedData = window.localStorage.getItem(PINNED_RULES_KEY)
    const SEEN_PINNED_KEY = `seenPinned-${tableId}`
    const seen = JSON.parse(window.localStorage.getItem(SEEN_PINNED_KEY) || '[]')

    let pinnedRules
    if (pinnedData === null) {
      // 首次造訪：套用全部 defaultPinned
      pinnedRules = supportRules.filter(r => r.defaultPinned).map(r => r.inputName)
    } else {
      // 既有使用者：尊重 pin 偏好，同時把「新增」的 defaultPinned（從未被 seen 過的）合進去
      // 這樣之後新增 defaultPinned rule 時，既有使用者也會看到 pinned，
      // 但使用者主動 unpin 後（那時已經 seen），不會再自動重 pin。
      const stored = JSON.parse(pinnedData)
      const newDefaultPinned = supportRules
        .filter(r => r.defaultPinned && !seen.includes(r.inputName))
        .map(r => r.inputName)
      pinnedRules = [...new Set([...stored, ...newDefaultPinned])]
    }

    // 持久化 pinned 與 seen（seen 一次記錄所有當前 defaultPinned，避免日後重 pin）
    window.localStorage.setItem(PINNED_RULES_KEY, JSON.stringify(pinnedRules))
    const allDefaultPinnedNow = supportRules.filter(r => r.defaultPinned).map(r => r.inputName)
    const nextSeen = [...new Set([...seen, ...allDefaultPinnedNow])]
    window.localStorage.setItem(SEEN_PINNED_KEY, JSON.stringify(nextSeen))

    for (const ruleInputName of pinnedRules) {
      if (supportRules.find(r => r.inputName === ruleInputName)) {
        addRule(ruleInputName)
      }
    }

    // 從 cache 還原的 query 中，補加尚未顯示的 rule，並回填表單值
    if (query && Object.keys(query).length > 0) {
      Object.keys(query).forEach(key => {
        if (key !== 'search' && supportRules.find(r => r.inputName === key)) {
          addRule(key);
        }
      });
      form.setFieldsValue(query);
    }
  }, [])


  const onPinRule = (inputName, pinned) => {
    let pinnedRules = JSON.parse(window.localStorage.getItem(PINNED_RULES_KEY)) || []
    if (pinned) {
      pinnedRules.push(inputName)
      notification.success({
        message: t('common.table.filters.filter_pinned_success'),
        placement: 'topRight',
        duration: 4.5,
      })
    } else {
      pinnedRules = pinnedRules.filter((rule) => rule !== inputName)
      notification.success({
        message: t('common.table.filters.filter_unpinned_success'),
        placement: 'topRight',
        duration: 4.5,
      })
    }
    pinnedRules = [...new Set(pinnedRules)]
    window.localStorage.setItem(PINNED_RULES_KEY, JSON.stringify(pinnedRules))
  }

  const pinnedRules = () => {
    const pinnedRules = JSON.parse(window.localStorage.getItem(PINNED_RULES_KEY)) || []
    return pinnedRules
  }


  const checkPrecondition = (rule) => {
    if (rule.precondition) {
      const values = form.getFieldsValue();
      // Check if the precondition field exists in current values and is truthy
      // Note: This assumes simple field names. Complex paths might need 'lodash/get' or similar.
      return !!values[rule.precondition];
    } 
    return true;
  }

  const options = supportRules.filter(({ inputName }) => !rulesInputNames.some((r) => r === inputName)).filter((rule)=> !rule.fixed)
    .filter((rule) => checkPrecondition(rule))


  const onFinish = (values) => {
    clearSelected();
    
    const cleanedValues = {};

    // Helper to get value by dot notation path
    const getValueByPath = (obj, path) => {
      // 1. Try direct access (flat key like "products.sku")
      if (Object.prototype.hasOwnProperty.call(obj, path)) {
        return obj[path];
      }
      // 2. Try nested access (nested object like { products: { sku: ... } })
      return path.split('.').reduce((acc, key) => (acc && acc[key] !== undefined) ? acc[key] : undefined, obj);
    };

    // 1. Handle explicit rules from supportRules
    supportRules.forEach(rule => {
      let val = getValueByPath(values, rule.inputName);
      
      // Handle String splitting for TextArea inputs (like SKU lists)
      if (typeof val === 'string') {
        val = val.trim();
        // Split by newline or comma if present
        if (val.includes('\n') || val.includes(',')) {
          val = val.split(/[\n,]/).map(v => v.trim()).filter(v => v !== '');
        }
      }

      if (val !== undefined && val !== null && val !== '') {
        // Special check for empty arrays (e.g. from Select multiple)
        if (Array.isArray(val) && val.length === 0) return;
        
        cleanedValues[rule.inputName] = val;
      }
    });
    
    // 2. Handle 'search' — KeywordSearchInput 一律 emit string[]
    if (Array.isArray(values.search) && values.search.length > 0) {
      cleanedValues.search = values.search;
    }

    setQuery(cleanedValues);
  };

  const hasFilters = searchable || supportRules.length > 0;
  const hasActions = newPath || onCreate || onImportClick;
  const shouldShowToolbar = hasFilters || hasActions;

  if (!shouldShowToolbar) {
    return null;
  }

  // Pure Actions Mode (No Filter Form needed)
  if (!hasFilters && hasActions) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'flex-end',
        gap: '8px',
        marginBottom: '12px'
      }}>
        {renderImportButton()}
        {renderCreateButton()}
      </div>
    );
  }

  const renderFilterHelpContent = () => {
    return (
      <div style={{ maxWidth: 300 }}>
        <Text strong style={{ display: 'block', marginBottom: 8 }}>
          {t('common.table.filters.filter_help.title')}
        </Text>
        <Text style={{ display: 'block', marginBottom: 8, whiteSpace: 'pre-line' }}>
          {t('common.table.filters.filter_help.content')}
        </Text>
        <Text type="secondary" style={{ display: 'block', fontSize: 12 }}>
          {filterHelpExample}
        </Text>
        {filterDocsUrl && (
          <a
            href={filterDocsUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={{ display: 'block', marginTop: 12, fontSize: 13 }}
          >
            {t('common.table.filters.filter_help.view_docs')}
          </a>
        )}
      </div>
    );
  };

  // 小螢幕：折疊式篩選器
  if (isMobile) {
    return (
      <div className="filter-panel-mobile">
        <Form
          form={form}
          onFinish={onFinish}
          className='form-horizontal'
        >
          {/* 搜尋框始終顯示 */}
          {searchable && <FilterComponent searchTooltipContent={searchTooltipContent} searchable={searchable} />}

          {/* 已套用的篩選 Tags */}
          {appliedFilters.length > 0 && (
            <div className="filter-applied-tags">
              {appliedFilters.map(({ key, label, value }) => (
                <Tag key={key} color="blue">
                  {label}: {value.length > 20 ? value.substring(0, 20) + '...' : value}
                </Tag>
              ))}
            </div>
          )}

          {/* 展開/收合按鈕 */}
          {supportRules.length > 0 && (
            <Button
              type="link"
              onClick={() => setIsFilterCollapsed(!isFilterCollapsed)}
              icon={isFilterCollapsed ? <DownOutlined /> : <UpOutlined />}
              style={{ padding: '8px 0', marginTop: '8px' }}
            >
              {isFilterCollapsed
                ? t('common.table.filters.expand_filters', { defaultValue: '展開篩選條件' })
                : t('common.table.filters.collapse_filters', { defaultValue: '收合篩選條件' })
              }
              {rulesInputNames.length > 0 && ` (${rulesInputNames.length})`}
            </Button>
          )}

          {/* 可折疊的篩選條件區域 */}
          {!isFilterCollapsed && supportRules.length > 0 && (
            <div style={{ marginTop: '8px' }}>
              <RulesForm options={options} supportRules={supportRules} onPinRule={onPinRule} pinnedRules={pinnedRules()} />
              {options.length > 0 && (
                <Button

                  onClick={() => addRule(options[0].inputName)}
                  type="dashed"
                  icon={<PlusOutlined />}
                  size="small"
                  style={{ marginTop: '8px' }}
                >
                  {t('common.table.filters.add_filter')}
                </Button>
              )}
            </div>
          )}

          {/* 底部按鈕區 - 響應式 */}
          <div className="filter-actions-mobile">
            <Button

              type="primary"
              htmlType="submit"
              loading={loading}
              icon={<SearchOutlined />}
              className="filter-action-primary"
              onClick={() => clearSelected()}
            >
              {t('common.table.filters.apply_filters')}
            </Button>

            {hasError && (
              <Button
                type="default"
                danger
                icon={<ClearOutlined />}
                onClick={clearAllSettings}
                className="filter-action-icon-only"
                title={t('common.table.clear_and_retry')}
              />
            )}

            {onImportClick && (
              <Button
                icon={<UploadOutlined />}
                onClick={onImportClick}
                className="filter-action-icon-only"
                title={t('common.button.import')}
                {...(testIdPrefix && { 'data-testid': `${testIdPrefix}-import-btn` })}
              />
            )}

            {(newPath || onCreate) && (
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => {
                  if (onCreate) onCreate();
                  else if (newPath) navigate(newPath);
                }}
                className="filter-action-icon-only"
                title={t('common.button.new')}
                {...(testIdPrefix && { 'data-testid': `${testIdPrefix}-create-btn` })}
              />
            )}
          </div>
        </Form>
      </div>
    );
  }

  // 桌面版：原本的佈局
  return (
    <div>
      <Form
        form={form}
        onFinish={onFinish}
        className='form-horizontal'
      >
        {searchable && <FilterComponent searchTooltipContent={searchTooltipContent} searchable={searchable} />}
        <RulesForm options={options} supportRules={supportRules} onPinRule={onPinRule} pinnedRules={pinnedRules()} />

        <div style={{
          display: 'flex',
          gap: '8px',
          alignItems: 'center',
          marginTop: '12px',
          paddingTop: '12px',
          borderTop: '1px solid #f0f0f0'
        }}>
          {options.length > 0 && (
            <Button
              onClick={() => addRule(options[0].inputName)}
              type="dashed"
              icon={<PlusOutlined />}
            >
              {t('common.table.filters.add_filter')}
            </Button>
          )}
          {(searchable || supportRules.length > 0) && (
            <Button

              type="primary"
              htmlType="submit"
              loading={loading}
              icon={<SearchOutlined />}
              onClick={() => {
                clearSelected()
              }}
            >
              {t('common.table.filters.apply_filters')}
            </Button>
          )}
          {hasError && (
            <Button
              type="default"
              danger
              icon={<ClearOutlined />}
              onClick={clearAllSettings}
            >
              清除所有設置
            </Button>
          )}

          <div style={{ marginLeft: 'auto', display: 'flex', gap: '8px', alignItems: 'center' }}>
            {supportRules.length > 0 && (
              <div>
                <Popover
                  content={renderFilterHelpContent()}
                  trigger="hover"
                  placement="bottomRight"
                >
                  <QuestionCircleOutlined style={{ fontSize: '16px', color: 'rgba(0, 0, 0, 0.45)', cursor: 'help' }} />
                </Popover>
              </div>
            )}
            {renderImportButton()}
            {renderCreateButton()}
          </div>
        </div>
      </Form>
    </div>
  );
}

export default Content;
